import React, { useCallback, useState } from 'react';
import clsx from 'clsx';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import Header from '../../components/Header';
import Loader from '../../components/Loader';

import {
  fetchKycDocIdData,
  initializeDigiLockerKyc,
  isDigiLockerStrategy,
} from '../../api/useKyc';
import { useUserStore } from '../../storage/userState';
import { EKycStatus } from '../../storage/store';
import { NAVIGATION_ROUTES } from '../../constant';
import { AADHAAR_DOC_TYPE } from '../../interfaces/Kyc';
import {
  getPendingKycSession,
  isKycApproved,
  isKycPending,
  setPendingKycSession,
  withPopupOrigin,
  type IKycPendingSession,
} from './helper';
import { useCompletePendingKyc } from './useCompletePendingKyc';
import { useDigiLockerPopup } from './useDigiLockerPopup';
import styles from './styles.module.css';

interface IKycLocationState {
  /** Where to return once verification resolves. */
  returnTo?: string;
  /** The user was mid-withdrawal when the gate sent them here. */
  resumeWithdraw?: boolean;
}

/**
 * Aadhaar KYC via DigiLocker.
 *
 * The backend returns a DigiLocker URL from `initializeDigiLockerKyc`, and it
 * opens in a popup so this page is never unloaded — a full-page redirect made
 * every return a cold load, and a cold load runs silent login, which on a failed
 * reauth wipes localStorage and takes the verification down with it. The
 * redirect remains the fallback for when a popup cannot be had.
 *
 * The session is still parked in localStorage regardless: the popup may be shut,
 * blocked, or handed off to the DigiLocker app, and the `clientId` is the only
 * way to finish afterwards.
 *
 * OTP and PAN flows are intentionally absent: the backend's verification
 * strategy is DigiLocker, and OTP is deprecated.
 */
const Kyc: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { userDetails } = useUserStore();

  const [isStarting, setIsStarting] = useState(false);

  const approved = isKycApproved(userDetails);
  const pending = isKycPending(userDetails);

  const { returnTo, resumeWithdraw } =
    (location.state as IKycLocationState) ?? {};

  // Carries the user back into the withdrawal they were blocked from, rather
  // than leaving them on a success screen to start over.
  const handleApproved = useCallback(
    (session: { returnTo?: string; resumeWithdraw?: boolean }) => {
      navigate(session.returnTo || NAVIGATION_ROUTES.WALLET, {
        state: { autoWithdraw: Boolean(session.resumeWithdraw) },
      });
    },
    [navigate],
  );

  const { isCompleting, hasPending, retry } = useCompletePendingKyc({
    onApproved: handleApproved,
  });

  // A closed popup and a lost message are indistinguishable from here, so both
  // outcomes run the same completion attempt. Trying on an abandoned session is
  // safe — the backend answers `aadhaar_consent_missing`, which leaves the
  // session parked for another attempt.
  const { openPlaceholder, navigateToVerification, abort, isAwaitingPopup } =
    useDigiLockerPopup({
      onResolved: ({ clientId }) => {
        // Re-park from the message when the parked session has lapsed: a user
        // slow through DigiLocker can outlast its TTL, and the popup's clientId
        // is then the only surviving copy. Losing it strands a verification the
        // user actually completed, which is unrecoverable without a webhook.
        if (clientId && !getPendingKycSession()) {
          setPendingKycSession({
            clientId,
            // Aadhaar for every flow this screen starts, same as the return-URL
            // fallback in useCompletePendingKyc.
            docType: AADHAAR_DOC_TYPE,
            returnTo: returnTo || NAVIGATION_ROUTES.WALLET,
            resumeWithdraw,
          });
        }
        retry();
      },
      onUnresolved: retry,
    });

  /**
   * The session to send the user to, reusing one already paid for when possible.
   *
   * Every SurePass `initialize` is billed, so a second attempt must reopen the
   * existing session rather than start another. `getPendingKycSession` drops
   * expired entries, so anything it hands back is still live.
   */
  const resolveSession = async (): Promise<IKycPendingSession | null> => {
    const parked = getPendingKycSession();
    if (parked?.url) return parked;

    const { documentType, verificationStrategy } = await fetchKycDocIdData();

    if (!isDigiLockerStrategy(verificationStrategy)) {
      // Only OTP opts out, and that flow is not built here.
      toast.info(t('kycUnavailableHere'));
      return null;
    }

    const docType = documentType ?? AADHAAR_DOC_TYPE;
    const started = await initializeDigiLockerKyc(docType);

    if (!started.url || !started.clientId) {
      toast.error(t('errorOccurred'));
      return null;
    }

    const session: IKycPendingSession = {
      clientId: started.clientId,
      docType,
      url: started.url,
      returnTo: returnTo || NAVIGATION_ROUTES.WALLET,
      resumeWithdraw,
    };

    // Parked before the popup goes anywhere: if it is shut, blocked, or the user
    // finishes in the DigiLocker app and comes back by hand, this is the only
    // record that a verification is outstanding.
    setPendingKycSession(session, started.expirySeconds);
    return session;
  };

  const handleVerify = async () => {
    if (isStarting || isAwaitingPopup) return;

    // Synchronously, before any await. A popup opened after the init round trip
    // has lost the user gesture and the browser blocks it.
    const popup = openPlaceholder();
    setIsStarting(true);

    try {
      const session = await resolveSession();
      if (!session?.url) {
        abort();
        return;
      }

      if (!popup || !navigateToVerification(withPopupOrigin(session.url))) {
        // Blocked, or the window went away while init was in flight. Fall back
        // to the full-page redirect, which still completes on the way back in.
        abort();
        window.location.href = session.url;
        return;
      }
    } catch (error) {
      abort();
      toast.error((error as Error)?.message || t('errorOccurred'));
    } finally {
      setIsStarting(false);
    }
  };

  if (isCompleting) return <Loader />;

  const statusClass = approved
    ? styles.statusApproved
    : pending
    ? styles.statusPending
    : styles.statusFailed;

  // A parked session no longer blocks the button: it is reopened rather than
  // replaced, so clicking again resumes the verification already paid for
  // instead of orphaning its clientId behind a new one.
  const canVerify = !approved && !pending;

  return (
    <div className={styles.kycPage}>
      <Header
        pageTitle={t('kycTitle')}
        routeValue={returnTo || NAVIGATION_ROUTES.WALLET}
      />

      <div className={styles.kycBody}>
        <div className={styles.statusCard}>
          <p className={clsx(styles.statusLabel, 'fontSizeSm12 fontColor666')}>
            {t('kycStatusLabel')}
          </p>
          <p
            className={clsx(statusClass, styles.statusText, 'fontWeightBold fontSizeSm')}
          >
            {userDetails?.kycStatus?.kycStatusText ||
              userDetails?.kycStatus?.status ||
              EKycStatus.FAILED}
          </p>
          <p className={clsx(styles.statusHint, 'fontSizeSm12 fontColor666')}>
            {userDetails?.kycStatus?.message || t('kycVerifyHint')}
          </p>
        </div>
      </div>

      <div className={styles.ctaBar}>
        {approved ? (
          <button
            type="button"
            className="btn primaryBtn"
            onClick={() =>
              navigate(returnTo || NAVIGATION_ROUTES.WALLET, {
                state: { autoWithdraw: Boolean(resumeWithdraw) },
              })
            }
          >
            {t('goToWallet')}
          </button>
        ) : (
          <button
            type="button"
            className="btn primaryBtn"
            disabled={isStarting || isAwaitingPopup || !canVerify}
            onClick={handleVerify}
          >
            {isStarting
              ? t('pleaseWait')
              : isAwaitingPopup
              ? t('kycPopupWaiting')
              : hasPending
              ? t('kycContinueCta')
              : t('kycVerifyCta')}
          </button>
        )}

        {/* Covers a redirect that never comes back — the user finishes in
            DigiLocker, returns by hand, and confirms from here. */}
        {hasPending && !approved && (
          <button
            type="button"
            className={clsx(styles.linkBtn, 'fontSizeSm12')}
            disabled={isCompleting}
            onClick={retry}
          >
            {t('kycAlreadyVerified')}
          </button>
        )}
      </div>
    </div>
  );
};

export default Kyc;
